import React, { useMemo, useCallback } from 'react';

const DEFAULT_EXCLUDE_FIELDS = ['MODIFIED_DATE', 'MODIFIED_BY'];

const DEFAULT_HIGHLIGHT_STYLE = {
  backgroundColor: 'rgba(255, 167, 38, 0.25)',
  borderLeft: '3px solid #ffa726',
  fontWeight: 'bold',
};

/**
 * Compare two values as trimmed strings.
 */
function areValuesEqual(val1, val2) {
  if (val1 == null && val2 == null) return true;
  if (val1 == null || val2 == null) return false;
  return String(val1).trim() === String(val2).trim();
}

/**
 * Walk sorted data and build a Map of dateValue -> Set<fieldName> for changed
 * fields between consecutive versions.
 */
function identifyChangedFields(sorted, dateField, excludeFields) {
  const changes = new Map();
  if (sorted.length <= 1) return changes;

  const excludeSet = new Set(excludeFields);

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const previous = sorted[i + 1];
    const changed = new Set();

    Object.keys(current).forEach((field) => {
      if (excludeSet.has(field)) return;
      if (!areValuesEqual(current[field], previous[field])) {
        changed.add(field);
      }
    });

    if (changed.size > 0) {
      changes.set(current[dateField], changed);
    }
  }
  return changes;
}

/**
 * React hook that computes delta detection metadata for a history dataset.
 *
 * @param {Array}  data                   - Array of history records
 * @param {Object} [options]
 * @param {string} [options.dateField='MODIFIED_DATE']   - Field used for version ordering
 * @param {Array}  [options.excludeFields]               - Fields to skip in diff
 * @param {Object} [options.highlightStyle]              - Override cell highlight style
 *
 * @returns {{ sortedData, changedFields, getCellStyle, getCellTooltip }}
 */
export function useDeltaDetection(data, options = {}) {
  const {
    dateField = 'MODIFIED_DATE',
    excludeFields = DEFAULT_EXCLUDE_FIELDS,
    highlightStyle = DEFAULT_HIGHLIGHT_STYLE,
  } = options;

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort(
      (a, b) => new Date(b[dateField]) - new Date(a[dateField])
    );
  }, [data, dateField]);

  const changedFields = useMemo(
    () => identifyChangedFields(sortedData, dateField, excludeFields),
    [sortedData, dateField, excludeFields]
  );

  const getCellStyle = useCallback(
    (params) => {
      const fields = changedFields.get(params.data?.[dateField]);
      if (fields && fields.has(params.colDef.field)) {
        return highlightStyle;
      }
      return null;
    },
    [changedFields, dateField, highlightStyle]
  );

  const getCellTooltip = useCallback(
    (params, fieldNames) => {
      const fields = changedFields.get(params.data?.[dateField]);
      if (fields && fields.has(params.colDef.field)) {
        const name =
          (fieldNames && fieldNames[params.colDef.field]) ||
          params.colDef.headerName ||
          params.colDef.field;
        return `Field "${name}" was modified in this version`;
      }
      return params.value != null ? String(params.value) : '';
    },
    [changedFields, dateField]
  );

  return { sortedData, changedFields, getCellStyle, getCellTooltip };
}

/**
 * Create the pinned "Version" AG Grid column definition that shows version
 * number and change-count chip.
 *
 * Usage in columnDefs:
 *   [createVersionColumn({ sortedData, changedFields }), ...otherCols]
 *
 * @param {Object} options
 * @param {Array}  options.sortedData     - Sorted data (newest first)
 * @param {Map}    options.changedFields  - Map<dateValue, Set<field>>
 * @param {string} [options.dateField='MODIFIED_DATE']
 * @returns {Object} AG Grid column definition
 */
export function createVersionColumn(options = {}) {
  const { sortedData, changedFields, dateField = 'MODIFIED_DATE' } = options;

  return {
    headerName: 'Version',
    field: '_version',
    pinned: 'left',
    width: 100,
    sortable: false,
    filter: false,
    getQuickFilterText: () => '',
    cellRenderer: (params) => {
      const idx = sortedData.findIndex(
        (r) => r[dateField] === params.data[dateField]
      );
      const fields = changedFields.get(params.data[dateField]);
      const count = fields ? fields.size : 0;
      const version = sortedData.length - idx;

      // Inline JSX so consumers don't need an extra import
      const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 18,
        minWidth: 22,
        fontSize: 10,
        fontWeight: 'bold',
        borderRadius: 9,
        backgroundColor: '#ed6c02',
        color: '#fff',
        marginLeft: 4,
        padding: '0 5px',
      };

      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <strong style={{ fontSize: 12 }}>v{version}</strong>
          {count > 0 && <span style={chipStyle}>{count}</span>}
        </span>
      );
    },
  };
}
